import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Battery, ChevronRight, Coins, FlaskConical, Home, Leaf, Loader2, Package,
  Pill, Search, ShieldAlert, ShieldCheck, UtensilsCrossed, XCircle,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta } from "../components/service/ServiceBits";
import { LegalNav, NeedHelp } from "./LegalCenter";
import api from "../lib/api";

const STATUS_META = {
  allowed: { label: "Generally Allowed", cls: "bg-emerald-600/10 text-emerald-700 border-emerald-600/30", Icon: ShieldCheck },
  restricted: { label: "Restricted", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30", Icon: AlertTriangle },
  prohibited: { label: "Prohibited", cls: "bg-red-500/10 text-red-700 border-red-500/30", Icon: XCircle },
  destination_dependent: { label: "Check Destination Rules", cls: "bg-blue-500/10 text-blue-700 border-blue-500/30", Icon: ShieldAlert },
};

const CATEGORY_META = {
  "Dangerous Goods": { Icon: AlertTriangle, desc: "Explosives, flammable substances, certain chemicals and hazardous materials." },
  "Weapons": { Icon: ShieldAlert, desc: "Firearms, ammunition and other prohibited weapons are not accepted." },
  "Drugs & Controlled Substances": { Icon: FlaskConical, desc: "Illegal or controlled substances are prohibited or heavily restricted." },
  "Liquids & Chemicals": { Icon: FlaskConical, desc: "Certain chemicals, flammable liquids, hazardous liquids and aerosols may be prohibited or restricted." },
  "Batteries & Electronics": { Icon: Battery, desc: "Lithium batteries, power banks and certain devices have carrier-specific restrictions." },
  "Food": { Icon: UtensilsCrossed, desc: "Food is not automatically prohibited — acceptance depends on product, destination, quantity, packaging, customs, quarantine rules and courier partner.", link: ["/nri-courier", "See NRI Courier Services"] },
  "Medicines": { Icon: Pill, desc: "Medicines may be restricted depending on destination, prescription requirements, quantity and customs regulations." },
  "Cash & Valuables": { Icon: Coins, desc: "Cash, precious metals, jewellery and high-value goods carry restrictions." },
  "Illegal & Counterfeit": { Icon: XCircle, desc: "Illegal goods, counterfeit products, stolen goods and items prohibited under applicable law." },
  "Wildlife & Biological": { Icon: Leaf, desc: "Wildlife products, animal products, plants, seeds and biological materials are restricted." },
  "Other Restricted": { Icon: Package, desc: "Destination and courier-specific restrictions maintained by the IndiaFast team." },
};

export default function ProhibitedPage() {
  const [page, setPage] = useState(null);
  const [items, setItems] = useState([]);
  const [contact, setContact] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  usePageMeta("Prohibited & Restricted Items | IndiaFast Global Couriers",
    "Know what you can and cannot send before booking your shipment — prohibited and restricted item categories, destination rules and the Can-I-Ship-This checker.", "/p/prohibited");

  useEffect(() => {
    api.get("/legal/public/prohibited").then((r) => setPage(r.data)).catch(() => {});
    api.get("/legal/public/prohibited/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
  }, []);

  const check = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try { const r = await api.get(`/legal/public/prohibited/search?q=${encodeURIComponent(q.trim())}`); setResults(r.data); }
    catch { setResults([]); }
    finally { setSearching(false); }
  };

  const byCategory = useMemo(() => {
    const map = {};
    for (const it of items) (map[it.category] = map[it.category] || []).push(it);
    return map;
  }, [items]);

  if (!page) return <div className="grid min-h-screen place-items-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="legal-prohibited-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-16 pt-32 sm:pt-36" data-testid="prohibited-hero">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-red-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <nav className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400" data-testid="legal-breadcrumb">
            <Link to="/" className="flex items-center gap-1 hover:text-saffron"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" /> <span>Legal</span>
            <ChevronRight className="h-3 w-3" /> <span className="text-saffron">{page.title}</span>
          </nav>
          <div className="mt-4 flex flex-wrap items-center gap-5">
            <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl border border-red-400/30 bg-red-500/10 text-red-400"><ShieldAlert className="h-8 w-8" /></span>
            <div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="legal-title">{page.title}</h1>
              <p className="mt-2 max-w-xl text-sm text-slate-300 sm:text-base">{page.subtitle}</p>
            </div>
          </div>
          {page.last_updated && <span className="mt-4 inline-block rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-bold text-slate-300">Last Updated: {page.last_updated}</span>}
          <div className="mt-6 flex gap-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4" data-testid="prohibited-warning">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400" />
            <p className="text-xs leading-relaxed text-amber-200 sm:text-sm">
              <span className="font-bold">Important:</span> Shipment acceptance is subject to applicable law, customs regulations, courier-partner rules and destination-country requirements. An item accepted in one country or by one courier partner may be restricted elsewhere.
            </p>
          </div>
        </div>
      </section>

      <LegalNav current="prohibited" />

      {/* CAN I SHIP THIS */}
      <section className="mx-auto max-w-3xl px-4 pt-12 sm:px-6" data-testid="ship-checker">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="tricolor-beam h-1.5" />
          <div className="p-6 sm:p-8">
            <h2 className="text-center font-heading text-xl font-extrabold text-navy sm:text-2xl">Can I Ship This?</h2>
            <p className="mt-1 text-center text-xs text-slate-500">Search the IndiaFast restricted-items database — maintained by our team.</p>
            <form onSubmit={check} className="mt-5 flex gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-xl border border-slate-300 px-4 focus-within:border-saffron">
                <Search className="h-4 w-4 shrink-0 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ship-checker-input"
                       placeholder="Enter an item name — e.g. pickle, battery, perfume, medicine, laptop"
                       className="w-full bg-transparent py-3 text-sm text-navy outline-none placeholder:text-slate-400" />
              </div>
              <button type="submit" disabled={searching} data-testid="ship-checker-btn"
                      className="rounded-xl bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover disabled:opacity-60">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
              </button>
            </form>
            {results !== null && (
              <div className="mt-5" data-testid="ship-checker-results">
                {!results.length ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center" data-testid="ship-checker-none">
                    <Package className="mx-auto h-7 w-7 text-slate-400" />
                    <p className="mt-2 text-sm font-bold text-navy">"{q}" isn't in our database yet</p>
                    <p className="mt-1 text-xs text-slate-500">Acceptance may depend on destination and courier partner — please confirm with our team before booking.</p>
                    <Link to="/support" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-navy px-5 py-2 text-xs font-bold text-white hover:bg-express">Ask Our Team <ChevronRight className="h-3.5 w-3.5" /></Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.map((it) => {
                      const sm = STATUS_META[it.status] || STATUS_META.restricted;
                      return (
                        <div key={it.id} className={`rounded-xl border p-4 ${sm.cls}`} data-testid={`ship-result-${it.id}`}>
                          <div className="flex items-center gap-2">
                            <sm.Icon className="h-4 w-4 shrink-0" />
                            <p className="text-sm font-extrabold">{it.name}</p>
                            <span className="ml-auto rounded-full bg-white/70 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">{sm.label}</span>
                          </div>
                          <p className="mt-1 text-[11px] font-semibold opacity-80">{it.category}</p>
                          <div className="mt-2 space-y-1 text-[11px] leading-relaxed opacity-90">
                            {it.domestic_allowed && <p><span className="font-bold">Domestic:</span> {it.domestic_allowed}</p>}
                            {it.international_allowed && <p><span className="font-bold">International:</span> {it.international_allowed}</p>}
                            {it.specific_countries && <p><span className="font-bold">Specific countries:</span> {it.specific_countries}</p>}
                            {it.packaging_requirements && <p><span className="font-bold">Packaging:</span> {it.packaging_requirements}</p>}
                            {it.documentation_requirements && <p><span className="font-bold">Documentation:</span> {it.documentation_requirements}</p>}
                            {it.courier_restrictions && <p><span className="font-bold">Courier partners:</span> {it.courier_restrictions}</p>}
                            {it.notes && <p>{it.notes}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="prohibited-categories">
        <h2 className="text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Restricted Item Categories</h2>
        <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(CATEGORY_META).map(([cat, meta], i) => {
            const catItems = byCategory[cat] || [];
            return (
              <div key={cat} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl" data-testid={`prohibited-cat-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-red-500/10 text-red-500"><meta.Icon className="h-5 w-5" /></span>
                  <h3 className="font-heading text-base font-bold text-navy">{cat}</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-slate-500">{meta.desc}</p>
                {!!catItems.length && (
                  <div className="mt-3 space-y-1.5">
                    {catItems.slice(0, 5).map((it) => {
                      const sm = STATUS_META[it.status] || STATUS_META.restricted;
                      return (
                        <p key={it.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-[11px]" data-testid={`prohibited-cat-item-${it.id}`}>
                          <span className="font-semibold text-navy">{it.name}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${sm.cls}`}>{sm.label}</span>
                        </p>
                      );
                    })}
                    {catItems.length > 5 && <p className="text-[10px] font-semibold text-slate-400">+{catItems.length - 5} more — use the search above</p>}
                  </div>
                )}
                {meta.link && (
                  <Link to={meta.link[0]} className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-saffron hover:text-saffron-hover">
                    {meta.link[1]} <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Editable intro sections */}
      <section className="mx-auto max-w-3xl px-4 pb-14 sm:px-6" data-testid="prohibited-sections">
        {page.sections.map((s, i) => (
          <div key={i} className="mb-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`prohibited-section-${i}`}>
            <h2 className="font-heading text-lg font-extrabold text-navy">{s.heading}</h2>
            {s.body && <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.body}</p>}
            {!!(s.bullets || []).length && (
              <ul className="mt-3 space-y-2">{s.bullets.map((b, j) => <li key={j} className="flex gap-2 text-sm text-slate-600"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{b}</li>)}</ul>
            )}
            {s.link_href && <Link to={s.link_href} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white hover:bg-express">{s.link_label || "Learn more"} <ChevronRight className="h-3.5 w-3.5" /></Link>}
          </div>
        ))}
        <div className="flex flex-wrap gap-2" data-testid="legal-crosslinks">
          {[["Shipping Policy", "/p/shipping"], ["NRI Courier", "/nri-courier"], ["Contact IndiaFast", "/support"]].map(([l, to]) => (
            <Link key={to} to={to} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-navy hover:border-saffron hover:text-saffron">{l} <ChevronRight className="h-3 w-3" /></Link>
          ))}
        </div>
      </section>

      <NeedHelp contact={contact} />
      <Footer contact={contact} />
    </div>
  );
}
